import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-contractors-janus-application',
  templateUrl: './contractors-janus-application.component.html',
  styleUrls: ['./contractors-janus-application.component.scss'],
  standalone: false
})
export class ContractorsJanusApplicationComponent implements OnInit, OnDestroy {
  @Input() row: any;

  isLoading = false;
  navigateToStepperForm = false;
  navigateToSummary = false;
  applicationSummaryDetails: any;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    if (!this.row?.createdApplicationId) return;

    this.route.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params.get('id') !== this.row.createdApplicationId) return;

        if (params.get('showAppView') === '1') {
          this.navigateToSummary = false;
          this.applicationSummaryDetails = null;
          this.navigateToStepperForm = true;
          if (!params.get('activityDataId')) {
            this.loadCurrentActivity(this.row.createdApplicationId);
          }
        } else if (params.get('showAppView') === '2') {
          this.navigateToStepperForm = false;
          this.loadApplicationSummary(this.row.createdApplicationId);
        }
      });

    if (this.route.snapshot.queryParamMap.get('id') === this.row.createdApplicationId &&
        ['1', '2'].includes(this.route.snapshot.queryParamMap.get('showAppView') || '')) return;

    if (this.isPendingApplication()) {
      this.loadApplication(this.row.createdApplicationId);
    } else {
      this.loadApplicationSummary(this.row.createdApplicationId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isPendingApplication(): boolean {
    return this.row?.status === 'Pending' || this.row?.status === 1;
  }

  onApplicationReopened(): void {
    this.navigateToSummary = false;
    this.applicationSummaryDetails = null;
    this.loadCurrentActivity(this.row.createdApplicationId);
  }

  private loadApplicationSummary(applicationId: string): void {
    this.isLoading = true;
    this.dataService.getApplicationSummary(applicationId, true, true, false)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isLoading = false))
      .subscribe({
        next: (summary: any) => {
          if (!summary?.application) return;
          if (summary.application.status === 0 || summary.application.status === 1) {
            this.navigateToSummary = false;
            this.loadCurrentActivity(applicationId);
            return;
          }
          this.applicationSummaryDetails = {
            application: summary.application,
            fees: summary.fees,
            totalPaid: summary.totalPaid,
            submitter: summary.submitter,
            completedActivities: summary.completedActivities,
            registrationId: summary.registrationId,
            client: summary.client,
            workflowType: summary.workflowType,
            workflowId: summary.workflowId,
            workflowVersionId: summary.workflowVersionId
          };
          this.navigateToSummary = true;
        },
        error: err => console.error(err)
      });
  }

  private loadApplication(applicationId: string): void {
    this.isLoading = true;
    this.dataService.isWorkflowApplicationInProgress(applicationId)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isLoading = false))
      .subscribe({
        // Registration status can still be Pending after the application completes.
        next: () => this.loadApplicationSummary(applicationId),
        error: err => console.error(err)
      });
  }

  private loadCurrentActivity(applicationId: string): void {
    this.dataService.getCurrentApplicationActivity(applicationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          let activityDataId: string | undefined;
          if (response.activity?.activityDataId) {
            activityDataId = response.activity.activityDataId;
          } else {
            const formActivity = response.navigationActivities
              ?.find((a: any) => a.activityType === 'form-activity');
            if (!formActivity) {
              console.error('No form activity found');
              return;
            }
            activityDataId = formActivity.activityDataId;
          }

          if (!activityDataId) return;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              id: applicationId,
              activityDataId,
              workflowId: response.workflowId,
              workflowVersionId: response.workflowVersionId,
              isTest: response.isTestApplication,
              showAppView: 1
            },
            queryParamsHandling: 'merge'
          });
          this.navigateToStepperForm = true;
        },
        error: err => console.error(err)
      });
  }
}
